import {quizActions, QuizProvider, useQuiz} from "../providers/QuizProvider.tsx";
import TextQuestion from "../components/quiz/TextQuestion.tsx";
import CheckboxQuestion from "../components/quiz/CheckboxQuestion.tsx";
import QuizNavigation from "../components/quiz/QuizNavigation.tsx";
import type {ReactNode} from "react";
import {trackContactProviderSubmission} from "../lib/algolia-insights.ts";

// Extend Window interface to include practice data
declare global {
    interface Window {
        EVEXIAS_CONTACT_PRACTICE?: {
            id: number;
            name: string;
            address: string;
            phone?: string;
            phone_formatted?: string;
            slug: string;
            external_id?: number;
        };
    }
}

interface FormStep {
    title: string;
    description?: string;
    content: ReactNode;
}

const ContactProviderFormContent = () => {
    const {state, dispatch} = useQuiz();

    // Get practice data from window object
    const practice = window.EVEXIAS_CONTACT_PRACTICE;

    if (!practice) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-800">
                <h3 className="font-semibold mb-2">Error: Provider Information Missing</h3>
                <p>Please ensure you access this form from a provider page.</p>
            </div>
        );
    }

    const steps: FormStep[] = [
        {
            title: `Contact ${practice.name}`,
            description: "Fill out the form below and the provider will reach out to you shortly",
            content: (
                <div className="space-y-6">
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="font-medium text-blue-900">{practice.name}</p>
                        <p className="text-sm text-blue-700 mt-1">{practice.address}</p>
                        {practice.phone_formatted && (
                            <p className="text-sm text-blue-700 mt-1">Phone: {practice.phone_formatted}</p>
                        )}
                    </div>

                    <TextQuestion name={'firstName'} label={'First Name'} type={"text"} required/>
                    <TextQuestion name={'lastName'} label={'Last Name'} type={"text"} required/>
                    <TextQuestion name={'phone'} label={'Phone Number'} type={"tel"} required/>
                    <TextQuestion name={'email'} label={'Email Address'} type={"email"} required/>

                    <CheckboxQuestion
                        name={'interestedIn'}
                        label={'I am interested in (select all that apply)'}
                        options={[
                            'Schedule Consultation',
                            'Schedule Blood Work',
                            'Hormone Optimizing Therapy',
                            'Peptide Therapy',
                            'Nutraceuticals'
                        ]}
                        required
                    />

                    <TextQuestion
                        name={'other'}
                        label={'Other'}
                        type={"text"}
                        placeholder="Any other interests..."
                    />

                    <TextQuestion
                        name={'message'}
                        label={'Message'}
                        placeholder="Tell us more about what brings you here..."
                        multiline
                    />
                </div>
            )
        }
    ];

    const currentStepData = steps[state.currentStep];

    // Check if current step can proceed
    const getStepValidation = () => {
        const {firstName, lastName, email, phone, interestedIn} = state.formData;
        const allRequiredFieldsFilled = firstName && lastName && email && phone &&
            (interestedIn as string[])?.length > 0;

        if (!allRequiredFieldsFilled) {
            return {
                canProceed: false,
                validationMessage: 'Please fill in all required fields',
                nextButtonText: 'Submit'
            };
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (email && !emailRegex.test(email)) {
            return {
                canProceed: false,
                validationMessage: 'Please enter a valid email address',
                nextButtonText: 'Submit'
            };
        }

        return {
            canProceed: true,
            nextButtonText: 'Submit'
        };
    };

    const validateCurrentStep = (): boolean => {
        return true;
    };

    const handleSubmit = async () => {
        // Prevent double submission
        if (state.isSubmitting) {
            return;
        }

        // Set submitting state to true
        dispatch(quizActions.setSubmitting(true));

        const {firstName, lastName, email, phone, interestedIn, other, message} = state.formData;

        const leadData = {
            practice_id: practice.id,
            source: "website",
            lead_type: "Contact Provider Form",
            data: {
                first_name: firstName,
                last_name: lastName,
                email: email,
                phone: phone,
                interested_in: (interestedIn as string[]).join(', '),
                other: other || '',
                message: message || ''
            }
        };


        trackContactProviderSubmission({
            practice_myevexias_id: practice.external_id,
            practice_name: practice.name,
            practice_nr_id: practice.id
        })

        try {
            const response = await fetch('https://evexias.nativerank.dev/api/leads', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(leadData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('Lead submitted successfully:', result);

            // Redirect back to provider page with success flag
            window.location.href = `/hormone-replacement-therapy-provider/${practice.slug}?results=2`;
        } catch (error) {
            console.error('Error submitting form:', error);
            dispatch(quizActions.setSubmitting(false));
            alert('There was an error submitting your form. Please try again or contact the provider directly.');
        }
    };

    if (state.error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                Error: {state.error}
            </div>
        );
    }

    const handleClose = () => {
        window.location.href = `/hormone-replacement-therapy-provider/${practice.slug}`;
    };

    return (
        <div className="max-w-2xl mx-auto relative">
            {/* Close button */}
            <button
                onClick={handleClose}
                className="fixed top-4 right-4 z-[60] p-2 bg-white rounded-full shadow-lg text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
                aria-label="Close form"
            >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
            </button>

            {/* Step content */}
            <div className="bg-white rounded-lg shadow-lg p-8 pb-0 md:pb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {currentStepData.title}
                </h2>
                {currentStepData.description && (
                    <p className="text-gray-600 mb-6">{currentStepData.description}</p>
                )}

                <div className="mt-6">
                    {currentStepData.content}
                </div>

                <QuizNavigation
                    totalSteps={steps.length}
                    onSubmit={handleSubmit}
                    validateStep={validateCurrentStep}
                    hidePreviousButton={true}
                    {...getStepValidation()}
                />
            </div>
        </div>
    );
};

const ContactProviderForm = () => {
    return (
        <QuizProvider>
            <div className="min-h-screen py-12">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 mb-2 contact-provider-form-title">Contact
                            Provider</h1>
                        <p className="text-gray-600 contact-provider-form-subtitle">Submit your information and the
                            provider will reach out to you</p>
                    </div>
                    <ContactProviderFormContent/>
                </div>
            </div>
        </QuizProvider>
    );
};

export default ContactProviderForm;